'use client';

import { Fragment, useEffect, useState } from 'react';
import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverGroup,
  PopoverPanel
} from '@headlessui/react';
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  UserIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import LocaleSwitcher from '@/components/LocaleSwitcher';
import Image from 'next/image';
import { shopifyAccountsURL, siteName } from '@/lib/env';
import Link from 'next/link';
import { type Menu } from '@/sanity/schemaTypes/objects/global/menuType';
import LinkInternal from './LinkInternal';
import { type Collection } from '@/sanity/schemaTypes/documents/collection';
import Cart from './cart/Cart';
import { type LayoutTranslations } from '@/sanity/schemaTypes/objects/translations/layoutTranslationsType';
import { motion, AnimatePresence } from 'motion/react';
import PortableText from '@/components/portableText/PortableText';
import { type PortableTextSimple } from '@/sanity/schemaTypes/portableText/portableTextSimpleType';

export default function Navbar({
  data,
  locale,
  translations
}: {
  data: { menu: Menu; topbar: { item: PortableTextSimple }[] };
  locale: string;
  translations: LayoutTranslations;
}) {
  const { menu, topbar } = data;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [currentTopbarIndex, setCurrentTopbarIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTopbarIndex((prevIndex) => (prevIndex < topbar.length - 1 ? prevIndex + 1 : 0));
    }, 3000);

    return () => clearInterval(interval);
  }, [topbar.length]);

  return (
    <div className="sticky top-0 z-40 bg-white">
      {/* Mobile menu */}
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="relative z-40 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/25 transition-opacity duration-300 ease-linear data-closed:opacity-0"
        />

        <div className="fixed inset-0 z-50 flex">
          <DialogPanel
            transition
            className="relative flex w-full max-w-xs transform flex-col overflow-y-auto bg-white pb-12 shadow-xl transition duration-300 ease-in-out data-closed:-translate-x-full"
          >
            <div className="flex px-4 pt-5 pb-2">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400"
              >
                <span className="sr-only">{translations.closeMenu}</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>

            <div className="space-y-6 px-4 py-6">
              {menu.links.map((link, i) => (
                <Fragment key={i}>
                  {link._type === 'collection' && (
                    <Disclosure as="div" className="-mx-3">
                      <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pr-3.5 pl-3 text-base/7 font-medium text-gray-700 hover:text-gray-800">
                        {link.title}
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="size-5 flex-none group-data-open:rotate-180"
                        />
                      </DisclosureButton>
                      <DisclosurePanel className="mt-2 space-y-2">
                        {link.children?.map((childCollection, i) => (
                          <Fragment key={i}>
                            <DisclosureButton
                              onClick={() => setMobileMenuOpen(false)}
                              as={Link}
                              href={childCollection.slug}
                              className="block rounded-lg py-2 pr-3 pl-6 text-sm/7 font-medium text-gray-900 hover:bg-gray-50 hover:text-gray-600"
                            >
                              {childCollection.title}
                            </DisclosureButton>
                            <div>
                              {childCollection.children?.map((grandChildCollection, i) => (
                                <DisclosureButton
                                  onClick={() => setMobileMenuOpen(false)}
                                  key={i}
                                  as={Link}
                                  href={grandChildCollection.slug}
                                  className="block rounded-lg py-2 pr-3 pl-12 text-sm/7 text-gray-500 hover:bg-gray-50 hover:text-gray-600"
                                >
                                  {grandChildCollection.title}
                                </DisclosureButton>
                              ))}
                            </div>
                          </Fragment>
                        ))}
                      </DisclosurePanel>
                    </Disclosure>
                  )}
                  {link._type === 'linkInternal' && (
                    <LinkInternal
                      link={link}
                      className="-m-2 block p-2 font-medium text-gray-900"
                    />
                  )}
                </Fragment>
              ))}
            </div>

            <div className="flex justify-end space-y-6 border-t border-gray-200 px-4 py-6">
              <LocaleSwitcher locale={locale} />
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <header className="relative z-10">
        <nav aria-label="Top">
          {/* Top bar */}
          <div className="bg-primary">
            <div className="mx-auto flex h-10 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
              <div className="text-secondary relative h-full flex-1 text-center text-sm font-medium">
                <AnimatePresence>
                  {topbar.map((item, i) =>
                    i === currentTopbarIndex ? (
                      <motion.div
                        key={i}
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -10, opacity: 0 }}
                        className="absolute inset-0 grid place-content-center"
                      >
                        <PortableText blocks={item.item} />
                      </motion.div>
                    ) : null
                  )}
                </AnimatePresence>
              </div>

              <div className="hidden lg:flex lg:items-center lg:justify-end lg:space-x-6">
                <LocaleSwitcher locale={locale} />
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="bg-white">
            <div className="border-b border-gray-200">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                  {/* Logo (lg+) */}
                  <div className="hidden lg:flex lg:items-center">
                    <Link href="/">
                      <span className="sr-only">{siteName}</span>
                      <Image
                        alt=""
                        src="/logo.svg"
                        unoptimized
                        width="1260"
                        height="573"
                        className="h-[4rem] w-[4.4rem]"
                      />
                    </Link>
                  </div>

                  <div className="hidden h-full lg:flex">
                    {/* Mega menus */}
                    <PopoverGroup className="ml-8">
                      <div className="flex h-full justify-center space-x-8">
                        {menu.links.map((link, i) => (
                          <Fragment key={i}>
                            {link._type === 'collection' && <CollectionPopover collection={link} />}
                            {link._type === 'linkInternal' && (
                              <LinkInternal
                                link={link}
                                className="flex items-center text-center text-sm font-medium text-gray-700 hover:text-gray-800"
                              />
                            )}
                          </Fragment>
                        ))}
                      </div>
                    </PopoverGroup>
                  </div>

                  {/* Mobile menu and search (lg-) */}
                  <div className="flex flex-1 items-center lg:hidden">
                    <button
                      type="button"
                      onClick={() => setMobileMenuOpen(true)}
                      className="-ml-2 rounded-md bg-white p-2 text-gray-400"
                    >
                      <span className="sr-only">{translations.openMenu}</span>
                      <Bars3Icon aria-hidden="true" className="size-6" />
                    </button>

                    {/* Search */}
                    <Link href="/recherche" className="ml-2 p-2 text-gray-400 hover:text-gray-500">
                      <span className="sr-only">{translations.search}</span>
                      <MagnifyingGlassIcon aria-hidden="true" className="size-6" />
                    </Link>
                  </div>

                  {/* Logo (lg-) */}
                  <Link href="/" className="lg:hidden">
                    <span className="sr-only">{siteName}</span>
                    <Image
                      alt=""
                      src="/logo.svg"
                      unoptimized
                      width="1260"
                      height="573"
                      className="h-[4rem] w-[4.4rem]"
                    />
                  </Link>

                  <div className="flex flex-1 items-center justify-end">
                    <div className="flex items-center lg:ml-8">
                      <div className="flex space-x-8">
                        <div className="hidden lg:flex">
                          <Link
                            href="/recherche"
                            className="-m-2 p-2 text-gray-400 hover:text-gray-500"
                          >
                            <span className="sr-only">{translations.search}</span>
                            <MagnifyingGlassIcon aria-hidden="true" className="size-6" />
                          </Link>
                        </div>

                        <div className="flex">
                          <Link
                            href={shopifyAccountsURL}
                            className="-m-2 p-2 text-gray-400 hover:text-gray-500"
                          >
                            <span className="sr-only">{translations.account}</span>
                            <UserIcon aria-hidden="true" className="size-6" />
                          </Link>
                        </div>
                      </div>

                      <span aria-hidden="true" className="mx-4 h-6 w-px bg-gray-200 lg:mx-6" />

                      <div className="flow-root">
                        <Cart locale={locale} translations={translations} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </div>
  );
}

function CollectionPopover({ collection }: { collection: Collection }) {
  return (
    <Popover className="flex">
      <div className="relative flex">
        <PopoverButton className="data-open:border-secondary data-open:text-secondary relative z-10 -mb-px flex cursor-pointer items-center border-b-2 border-transparent pt-px text-sm font-medium text-gray-700 transition-colors duration-200 ease-out hover:text-gray-800">
          {collection.title}
        </PopoverButton>
        <PopoverBackdrop className="fixed inset-0" />
      </div>

      <PopoverPanel
        transition
        className="absolute inset-x-0 top-full transition before:absolute before:inset-0 before:top-1/2 before:bg-white before:shadow-sm data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in sm:text-sm"
      >
        <div className="relative bg-white">
          <div className="mx-auto max-w-7xl px-8">
            <div className="grid grid-cols-4 items-start gap-x-8 gap-y-5 pt-10 pb-12">
              {collection.children?.map((childCollection, i) => (
                <div key={i} className="flex flex-col items-start">
                  <CloseButton
                    as={Link}
                    href={childCollection.slug}
                    className="group relative inline font-medium text-gray-900"
                  >
                    {childCollection.title}
                    <div className="absolute inset-y-0 right-full mr-2 grid w-3 place-content-center opacity-0 duration-200 ease-out group-hover:opacity-100">
                      <ChevronRightIcon aria-hidden="true" className="size-3" />
                    </div>
                  </CloseButton>
                  {childCollection.children?.map((grandChildCollection, i) => (
                    <CloseButton
                      as={Link}
                      key={i}
                      href={grandChildCollection.slug}
                      className="group relative text-gray-500 hover:text-gray-600"
                    >
                      {grandChildCollection.title}
                      <div className="absolute inset-y-0 right-full mr-2 grid w-3 place-content-center opacity-0 duration-200 ease-out group-hover:opacity-100">
                        <ChevronRightIcon aria-hidden="true" className="size-3" />
                      </div>
                    </CloseButton>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
}
